/**
 * Work out which OLA banners are inside a picked folder.
 *
 * A user drops one folder containing four ads, or a folder containing one ad,
 * or — in every real delivery seen so far — a folder of folders where each ad
 * is itself nested one level deeper than the name suggests
 * (`Capri_300x250/Capri_300x250/index.html`). Rather than special-casing any of
 * that, every `index.html` in the tree roots an ad and every file is assigned
 * to the deepest ad root that prefixes it. Nesting then costs nothing.
 *
 * Pure and DOM-free so it can be tested by calling it: the interesting
 * behaviour is entirely in how paths are grouped and which ads are refused.
 *
 * Mirrors the server rules in `apps/asset-library/backend/src/core/storage.py`
 * (`is_safe_ola_relpath`) and `src/validation/ola.py`. This copy exists to
 * refuse a bad folder before any bytes move; the backend enforces all of it
 * again, so a gap here is a worse error message, never a hole.
 */

import { formatSize } from './constants';

export interface PickedFile {
  file: File;
  /** Folder-relative, forward-slashed, no leading slash. */
  path: string;
}

export interface OlaMember {
  file: File;
  /** Path relative to *this ad's* root — the literal string the HTML asks for. */
  relpath: string;
}

export interface DetectedAd {
  /** Last segment of the ad root, and what the asset is named. */
  name: string;
  /** Path prefix the ad lives under, '' when the picked folder is itself the ad. */
  root: string;
  entryRelpath: string;
  /** Null means the ad cannot be uploaded — see the hard block below. */
  posterRelpath: string | null;
  files: OlaMember[];
  totalBytes: number;
  /** Empty means uploadable. Anything here is shown against the ad and blocks it. */
  issues: string[];
}

export interface OlaDetection {
  ads: DetectedAd[];
  /** Files under no ad root, named so the user can see what was ignored. */
  skipped: string[];
}

/** `MAX_OLA_MEMBER_BYTES` in `src/validation/ola.py`. */
export const MAX_OLA_MEMBER_BYTES = 32 * 1024 * 1024;
/** `MAX_OLA_TOTAL_BYTES`. */
export const MAX_OLA_TOTAL_BYTES = 128 * 1024 * 1024;
/** `MAX_OLA_MEMBERS_PER_AD`. */
export const MAX_OLA_MEMBERS_PER_AD = 200;
/** `MAX_OLA_ADS_PER_BATCH`. */
export const MAX_OLA_ADS_PER_BATCH = 40;
/** `MAX_OLA_RELPATH_DEPTH`. */
const MAX_RELPATH_DEPTH = 8;
/** `MAX_OLA_RELPATH_LENGTH`. */
const MAX_RELPATH_LENGTH = 300;

/** The keys of the backend's `EXTENSION_TO_OLA_TYPE`; it derives the MIME itself. */
const OLA_MEMBER_EXTENSIONS = new Set([
  '.html',
  '.htm',
  '.css',
  '.js',
  '.mjs',
  '.json',
  '.woff',
  '.woff2',
  '.ttf',
  '.otf',
  '.eot',
  '.svg',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.mp4',
  '.webm',
  '.ico',
  '.txt',
  '.xml',
]);

const ENTRY_FILENAME = 'index.html';
const POSTER_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

/** Basenames every Mac and Windows delivery carries and no banner references. */
const JUNK_BASENAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini', '.localized']);

export function basename(path: string): string {
  const slash = path.lastIndexOf('/');
  return slash === -1 ? path : path.slice(slash + 1);
}

function extension(path: string): string {
  const name = basename(path).toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? '' : name.slice(dot);
}

/** Normalise a browser-supplied path to the forward-slashed, relative form. */
export function normalisePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+/, '');
}

/**
 * Files no banner references and every delivery contains.
 *
 * `__MACOSX` in particular is not cosmetic: the backend rejects the whole ad if
 * one arrives, because it will not depend on the client having stripped it.
 */
export function isJunk(path: string): boolean {
  const segments = path.split('/');
  if (segments.some((segment) => segment === '__MACOSX')) return true;
  if (segments.some((segment) => segment.startsWith('._'))) return true;
  return JUNK_BASENAMES.has(basename(path).toLowerCase());
}

/**
 * The client-side copy of `is_safe_ola_relpath`.
 *
 * Reject, never rewrite — the inverse of how ordinary filenames are handled in
 * this app. `index.html` asks for the literal string `assets/t_bg1.jpg`, so a
 * path that has been sanitised into something else names an object the banner
 * will never request.
 */
export function isSafeRelpath(relpath: string): boolean {
  if (!relpath || relpath.length > MAX_RELPATH_LENGTH) return false;
  if (relpath.startsWith('/') || relpath.includes('\\') || relpath.includes(':')) return false;

  const segments = relpath.split('/');
  if (segments.length > MAX_RELPATH_DEPTH) return false;

  return segments.every(
    (segment) =>
      segment.length > 0 &&
      segment.length <= 121 &&
      segment === segment.trim() &&
      // A leading underscore is allowed and a leading dot is not, which is what
      // admits `_backup.jpg` while excluding `.`, `..` and every dotfile.
      /^[A-Za-z0-9_][A-Za-z0-9._ -]*$/.test(segment),
  );
}

/**
 * Pick the fallback still, looking only at the ad root.
 *
 * Root-only on purpose: `assets/backup-copy.jpg` is a source file, not the
 * still the ad server shows, and picking it would put a wrong image on the
 * grid tile. Sorted before the loose match so the choice is deterministic when
 * a folder somehow has two.
 */
function findPoster(relpaths: string[]): string | null {
  const rootFiles = relpaths.filter((relpath) => !relpath.includes('/'));
  const byPreference = ['_backup.jpg', 'backup.jpg', '_backup.png', 'backup.png'];

  for (const preferred of byPreference) {
    const match = rootFiles.find((relpath) => relpath.toLowerCase() === preferred);
    if (match) return match;
  }

  return (
    [...rootFiles]
      .sort()
      .find(
        (relpath) =>
          relpath.toLowerCase().includes('backup') &&
          POSTER_EXTENSIONS.includes(extension(relpath)),
      ) ?? null
  );
}

/**
 * Group a picked file list into banners.
 *
 * Never throws and never silently drops an ad: an unusable one comes back with
 * its reasons in `issues` so the modal can show it greyed out. A user who
 * picked a folder of four and sees three has to be told which one is missing
 * and why.
 */
export function detectOlaAds(entries: PickedFile[]): OlaDetection {
  const usable = entries
    .map(({ file, path }) => ({ file, path: normalisePath(path || file.name) }))
    .filter(({ path }) => !isJunk(path));

  // Every index.html roots an ad. Deepest-first, so the assignment loop below
  // can take the first prefix it matches.
  const roots = usable
    .filter(({ path }) => basename(path).toLowerCase() === ENTRY_FILENAME)
    .map(({ path }) => path.slice(0, Math.max(0, path.length - ENTRY_FILENAME.length - 1)))
    .sort((a, b) => b.length - a.length);

  const unique = [...new Set(roots)];
  const skipped: string[] = [];

  const grouped = new Map<string, PickedFile[]>(unique.map((root) => [root, []]));

  for (const entry of usable) {
    const root = unique.find(
      (candidate) => candidate === '' || entry.path.startsWith(`${candidate}/`),
    );
    if (root === undefined) {
      skipped.push(entry.path);
      continue;
    }
    grouped.get(root)!.push(entry);
  }

  // Shallowest first, so the list reads in the order the folders appear.
  const ads = unique
    .slice()
    .sort()
    .map((root) => buildAd(root, grouped.get(root) ?? []));

  if (ads.length > MAX_OLA_ADS_PER_BATCH) {
    for (const ad of ads.slice(MAX_OLA_ADS_PER_BATCH)) {
      ad.issues.push(`Over the ${MAX_OLA_ADS_PER_BATCH}-OLA limit for one upload`);
    }
  }

  return { ads, skipped };
}

function buildAd(root: string, entries: PickedFile[]): DetectedAd {
  const issues: string[] = [];
  const files: OlaMember[] = [];
  let totalBytes = 0;

  for (const { file, path } of entries) {
    const relpath = root === '' ? path : path.slice(root.length + 1);

    if (!isSafeRelpath(relpath)) {
      issues.push(`${relpath} — unusable file path`);
      continue;
    }
    if (!OLA_MEMBER_EXTENSIONS.has(extension(relpath))) {
      issues.push(`${relpath} — not a file type an OLA can contain`);
      continue;
    }
    if (file.size === 0) {
      issues.push(`${relpath} — file is empty`);
      continue;
    }
    if (file.size > MAX_OLA_MEMBER_BYTES) {
      issues.push(
        `${relpath} — ${formatSize(file.size)} is over the ${formatSize(
          MAX_OLA_MEMBER_BYTES,
        )} limit`,
      );
      continue;
    }

    files.push({ file, relpath });
    totalBytes += file.size;
  }

  if (files.length > MAX_OLA_MEMBERS_PER_AD) {
    issues.push(`${files.length} files is over the ${MAX_OLA_MEMBERS_PER_AD}-file limit per OLA`);
  }
  if (totalBytes > MAX_OLA_TOTAL_BYTES) {
    issues.push(`${formatSize(totalBytes)} is over the ${formatSize(MAX_OLA_TOTAL_BYTES)} limit`);
  }

  const relpaths = files.map((member) => member.relpath);
  const posterRelpath = findPoster(relpaths);

  // The hard block. Without a fallback still there is nothing to put on the
  // grid tile, nothing for an ad server to show when JavaScript is off, and
  // nothing to read the ad's dimensions from if `<meta name="ad.size">` is
  // missing too. The backend refuses these as well.
  if (posterRelpath === null) {
    issues.push('No fallback still (_backup.jpg) at the top level of this OLA');
  }

  return {
    // A root of '' means loose files were dropped rather than a folder picked,
    // so there is no folder name to take; the user renames it afterwards.
    name: root === '' ? 'OLA' : basename(root),
    root,
    entryRelpath:
      files.find((m) => m.relpath.toLowerCase() === ENTRY_FILENAME)?.relpath ?? ENTRY_FILENAME,
    posterRelpath,
    files,
    totalBytes,
    issues,
  };
}

/** An ad the modal may actually upload. */
export function isUploadable(ad: DetectedAd): boolean {
  return ad.issues.length === 0 && ad.posterRelpath !== null && ad.files.length > 0;
}
